import { useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { PageBanner } from "@/shared/components/PageBanner";
import { useAssignOperatorToRoster, useOperatorRosterAssignments, useResolveEffectiveShift, useRosters } from "../api";
import { useV1UsersList, useV1ProductionWorkCentersList } from "@/api/generated/v1/v1";
import type { Roster, OperatorRosterAssignment } from "../types";

export default function OperatorRosterAssignmentPage() {
  const { data: rosters = [] } = useRosters();
  const { data: assignments = [] } = useOperatorRosterAssignments();
  const assign = useAssignOperatorToRoster();

  const { data: usersData } = useV1UsersList({ page_size: 1000 } as any);
  const { data: wcData } = useV1ProductionWorkCentersList({ page_size: 1000 } as any);

  const users = usersData?.results || [];
  const workCenters = wcData?.results || [];

  const [form, setForm] = useState({ operator_user_id: "", roster: "", work_center: "", start_date: "" });
  const [lookup, setLookup] = useState({ operator_user_id: "", date: "" });
  const { data: resolved } = useResolveEffectiveShift(lookup.operator_user_id, lookup.date, !!lookup.operator_user_id && !!lookup.date);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", fontFamily: "var(--font-family-fa, sans-serif)" }} dir="rtl">
      <PageBanner
        title="تخصیص اپراتور به گروه‌های کاری (Assignments)"
        subtitle="تخصیص دوره‌ای یا روزانه اپراتورها به الگوهای شیفت در ایستگاه‌های کاری"
        icon="👤"
        helpLink="/help/shopfloor/assignments"
      />

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>تخصیص جدید (دوره‌ای، نه روزانه)</h2>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>اپراتور</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.operator_user_id}
              onChange={(e) => setForm((s) => ({ ...s, operator_user_id: e.target.value }))}
            >
              <option value="">— انتخاب اپراتور —</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.username})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>گروه کاری (Roster)</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.roster} onChange={(e) => setForm((s) => ({ ...s, roster: e.target.value }))}
            >
              <option value="">— انتخاب Roster —</option>
              {rosters.map((r: Roster) => (
                <option key={r.id} value={r.id}>
                  {r.code}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ایستگاه پیش‌فرض</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.work_center}
              onChange={(e) => setForm((s) => ({ ...s, work_center: e.target.value }))}
            >
              <option value="">— انتخاب ایستگاه —</option>
              {workCenters.map((wc: any) => (
                <option key={wc.id} value={wc.id}>
                  {wc.name} ({wc.code})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px", position: "relative" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>از تاریخ</label>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right"
              portal
              value={form.start_date || ""}
              onChange={(date: DateObject | null) => {
                setForm((prev) => ({
                  ...prev,
                  start_date: date ? date.format("YYYY-MM-DD") : "",
                }));
              }}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", height: "auto" }}
              containerStyle={{ width: "100%" }}
              placeholder="انتخاب تاریخ شمسی..."
            />
          </div>
          <div style={{ flex: "1 1 100%", display: "flex", justifyContent: "flex-end" }}>
            <button style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: assign.isPending ? "not-allowed" : "pointer", opacity: assign.isPending ? 0.7 : 1 }} onClick={() => assign.mutate(form)} disabled={assign.isPending}>
              تخصیص
            </button>
          </div>
        </div>
        {assign.isError && <p style={{ color: "#EF4444", fontSize: "0.85rem", marginTop: "1rem", fontWeight: 600 }}>{(assign.error as Error)?.message}</p>}
      </section>

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>تخصیص‌های فعلی</h2>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", textAlign: "right" }}>
            <thead>
              <tr style={{ background: "rgba(0,0,0,0.04)", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>اپراتور</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>Roster</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>ایستگاه پیش‌فرض</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>از تاریخ</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>تا تاریخ</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a: OperatorRosterAssignment) => {
                const op = users.find((u: any) => u.id === a.operator_user_id);
                const operatorName = op ? `${op.first_name} ${op.last_name}` : a.operator_user_id;

                return (
                  <tr key={a.id} style={{ borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                    <td style={{ padding: "12px 10px", color: "#444", fontWeight: 600 }}>{operatorName}</td>
                    <td style={{ padding: "12px 10px", color: "#444" }}>
                      <span style={{ padding: "4px 8px", borderRadius: "6px", background: "rgba(0,0,0,0.04)", fontWeight: 600 }}>{a.roster_display?.code}</span>
                    </td>
                    <td style={{ padding: "12px 10px", color: "#444" }}>{a.work_center_display?.name || a.work_center_display?.code || "—"}</td>
                    <td style={{ padding: "12px 10px", color: "#444" }}>{a.start_date}</td>
                    <td style={{ padding: "12px 10px", color: "#888" }}>{a.end_date ?? "تا اطلاع ثانوی"}</td>
                  </tr>
                );
              })}
              {assignments.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "#888" }}>هیچ تخصیصی یافت نشد.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>استعلام شیفت مؤثر یک نفر در یک تاریخ</h2>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>اپراتور</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={lookup.operator_user_id}
              onChange={(e) => setLookup((s) => ({ ...s, operator_user_id: e.target.value }))}
            >
              <option value="">— انتخاب اپراتور —</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.username})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px", position: "relative" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>تاریخ استعلام</label>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right"
              portal
              value={lookup.date || ""}
              onChange={(date: DateObject | null) => {
                setLookup((prev) => ({
                  ...prev,
                  date: date ? date.format("YYYY-MM-DD") : "",
                }));
              }}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", height: "auto" }}
              containerStyle={{ width: "100%" }}
              placeholder="انتخاب تاریخ شمسی..."
            />
          </div>
        </div>
        {resolved && (
          <div style={{ marginTop: "1.5rem", padding: "1rem", borderRadius: "10px", background: resolved.is_on_leave ? "rgba(245, 158, 11, 0.1)" : "rgba(16, 185, 129, 0.1)", border: `1px solid ${resolved.is_on_leave ? "rgba(245, 158, 11, 0.2)" : "rgba(16, 185, 129, 0.2)"}`, fontSize: "0.9rem", color: "#2B2B2B" }}>
            {resolved.is_on_leave ? (
              <span style={{ color: "#D97706", fontWeight: 700 }}>این نفر در این تاریخ مرخصی است.</span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <span style={{ fontWeight: 700, color: "#059669" }}>✓ شیفت مؤثر یافت شد:</span>
                <span style={{ color: "#444" }}>
                  <span style={{ fontWeight: 600, color: "#2B2B2B" }}>منبع:</span> {resolved.source} <span style={{ margin: "0 8px", color: "#CCC" }}>|</span>
                  <span style={{ fontWeight: 600, color: "#2B2B2B" }}>شیفت:</span> {resolved.shift_template_code ?? "—"} <span style={{ margin: "0 8px", color: "#CCC" }}>|</span>
                  <span style={{ fontWeight: 600, color: "#2B2B2B" }}>ایستگاه:</span> {resolved.work_center_code ?? "—"} <span style={{ margin: "0 8px", color: "#CCC" }}>|</span>
                  <span style={{ fontWeight: 600, color: "#2B2B2B" }}>زمان:</span> {resolved.start_time ?? "—"} تا {resolved.end_time ?? "—"}
                </span>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
